import {
  QueryClient,
  useQuery,
  useInfiniteQuery,
  keepPreviousData,
  queryOptions,
  type InfiniteData,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  api,
  type ApiKey,
  type ApiKeyUsageResponse,
  type AvailableModel,
  type EndpointSpend,
  type EndpointTraces,
  type PortalModelPricing,
  type PortalVideoModelPricing,
  type RequestHistory,
  type UsageStats,
} from './api';
import { historyQueryKey, historyRequestParams } from './features/requests/historyPagination';
import {
  analyticsPollInterval,
  clearAnalyticsPoll,
  observeAnalyticsResponse,
} from './analyticsPolling';

const BACKGROUND_PREFETCH_GAP_MS = 150;

// Create a global query client instance
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

function useAnalyticsPollScope(scope: string, enabled = true) {
  useEffect(() => {
    if (!enabled) clearAnalyticsPoll(scope);
    return () => clearAnalyticsPoll(scope);
  }, [scope, enabled]);
}

// Custom hooks for react-query

// User info
export function useMeQuery() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// API Keys
export function useKeysQuery() {
  const inventory = useQuery({
    queryKey: ['keys'],
    queryFn: ({ signal }) => api.keys(signal).then((res) => res.keys || []),
    staleTime: 60 * 1000, // 1 minute
    retry: false,
    refetchOnWindowFocus: false,
  });
  const inventoryRefs = (inventory.data || [])
    .map((key) => key.key_ref)
    .filter((ref): ref is string => !!ref)
    .sort();
  const usageQueryKey = ['keys', 'usage', inventoryRefs] as const;
  const usageScope = JSON.stringify(usageQueryKey);
  useAnalyticsPollScope(usageScope, inventory.isSuccess);
  const hasMissingUsage = (data: ApiKeyUsageResponse | undefined) => {
    const usageRefs = new Set((data?.usage || []).map((row) => row.key_ref));
    return inventoryRefs.some((ref) => !usageRefs.has(ref));
  };
  const usage = useQuery({
    queryKey: usageQueryKey,
    enabled: inventory.isSuccess,
    queryFn: async ({ signal }) => {
      const previous = queryClient.getQueryData<ApiKeyUsageResponse>(usageQueryKey);
      const response = await api.keyUsage(signal, {
        refreshId: previous?.refreshing ? previous.refresh_id : undefined,
        resultVersion: previous?.refreshing ? previous.result_version : undefined,
      });
      return observeAnalyticsResponse(
        usageScope,
        response,
        response.refreshing || hasMissingUsage(response),
      );
    },
    staleTime: 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      return analyticsPollInterval(
        usageScope,
        query.state.data,
        query.state.status === 'error',
        query.state.data?.refreshing || hasMissingUsage(query.state.data),
      );
    },
  });
  const spendByKey = new Map((usage.data?.usage || []).map((row) => [row.key_ref, row] as const));
  const data = inventory.data?.map((key): ApiKey => {
    const row = key.key_ref ? spendByKey.get(key.key_ref) : undefined;
    return {
      ...key,
      spend: row?.spend ?? null,
      cost_source: row?.cost_source,
      usage_state: usage.isError
        ? row
          ? 'stale'
          : 'failed'
        : row
          ? (usage.data?.data_state ?? 'warming')
          : 'warming',
    };
  });
  return { ...inventory, data, usageError: usage.error, refetchUsage: usage.refetch };
}

// Organizations
export function useOrganizationsQuery() {
  return useQuery({
    queryKey: ['organizations'],
    queryFn: () =>
      api.organizations().then((res) => {
        if (Array.isArray(res)) return res;
        return res.organizations || [];
      }),
    staleTime: 60 * 1000, // 1 minute
  });
}

// Organization detail
export function useOrganizationDetailQuery(id: string, params: URLSearchParams) {
  // Use param string in queryKey to differentiate pagination/filters
  const paramStr = params.toString();
  return useQuery({
    queryKey: ['organization', id, paramStr],
    queryFn: () => api.organizationPage(id, params),
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!id,
  });
}

// Endpoints
export function useEndpointsQuery() {
  return useQuery({
    queryKey: ['endpoints'],
    queryFn: () => api.endpoints().then((res) => res.endpoints || []),
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Endpoint detail
export function useEndpointQuery(id: string) {
  return useQuery({
    queryKey: ['endpoint', id],
    queryFn: () => api.endpoint(id),
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!id,
  });
}

// Endpoint spend
export function useEndpointSpendQuery(id: string, enabled = true) {
  const queryKey = ['endpointSpend', id] as const;
  const pollScope = JSON.stringify(queryKey);
  useAnalyticsPollScope(pollScope, !!id && enabled);
  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const previous = queryClient.getQueryData<EndpointSpend>(queryKey);
      const response = await api.endpointSpend(id, signal, {
        refreshId: previous?.refreshing ? previous.refresh_id : undefined,
        resultVersion: previous?.refreshing ? previous.result_version : undefined,
      });
      return observeAnalyticsResponse(pollScope, response);
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: !!id && enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      analyticsPollInterval(pollScope, query.state.data, query.state.status === 'error'),
  });
}

// Endpoint traces
export function useEndpointTracesInfiniteQuery(
  id: string,
  params: URLSearchParams,
  enabled = true,
) {
  const paramStr = params.toString();
  const queryKey = ['endpointTraces', id, paramStr] as const;
  const pollScope = JSON.stringify(queryKey);
  useAnalyticsPollScope(pollScope, !!id && enabled);
  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const queryParams = new URLSearchParams(paramStr);
      if (pageParam) {
        queryParams.set('cursor_ts', pageParam.ts);
        queryParams.set('cursor_id', pageParam.id);
      }
      const previous = queryClient.getQueryData<InfiniteData<EndpointTraces>>(queryKey);
      const firstPage = previous?.pages[0];
      const response = await api.endpointTraces(
        id,
        queryParams,
        signal,
        pageParam
          ? {}
          : {
              refreshId: firstPage?.refreshing ? firstPage.refresh_id : undefined,
              resultVersion: firstPage?.refreshing ? firstPage.result_version : undefined,
            },
        !!pageParam,
      );
      return pageParam ? response : observeAnalyticsResponse(pollScope, response);
    },
    initialPageParam: null as { ts: string; id: string } | null,
    getNextPageParam: (lastPage) => {
      return lastPage.next_cursor_ts && lastPage.next_cursor_id
        ? { ts: lastPage.next_cursor_ts, id: lastPage.next_cursor_id }
        : null;
    },
    staleTime: 5 * 1000, // 5 seconds (rapidly changing)
    enabled: !!id && enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      analyticsPollInterval(pollScope, query.state.data?.pages[0], query.state.status === 'error'),
  });
}

// Usage stats
export function useStatsQuery(params: URLSearchParams) {
  const paramStr = params.toString();
  const queryKey = ['stats', paramStr] as const;
  const pollScope = JSON.stringify(queryKey);
  useAnalyticsPollScope(pollScope);
  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const previous = queryClient.getQueryData<UsageStats>(queryKey);
      const response = await api.stats(params, signal, {
        refreshId: previous?.refreshing ? previous.refresh_id : undefined,
        resultVersion: previous?.refreshing ? previous.result_version : undefined,
      });
      return observeAnalyticsResponse(pollScope, response);
    },
    staleTime: 10 * 1000, // 10 seconds
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      analyticsPollInterval(pollScope, query.state.data, query.state.status === 'error'),
  });
}

// Request History
export function historyQueryOptions(params: URLSearchParams, trackPolling = true) {
  const queryKey = historyQueryKey(params);
  const pollScope = JSON.stringify(queryKey);
  return queryOptions({
    queryKey,
    queryFn: async ({ signal }) => {
      const previous = queryClient.getQueryData<RequestHistory>(queryKey);
      const response = await api.history(params, signal, {
        refreshId: previous?.refreshing ? previous.refresh_id : undefined,
        resultVersion: previous?.refreshing ? previous.result_version : undefined,
      });
      return trackPolling ? observeAnalyticsResponse(pollScope, response) : response;
    },
    staleTime: 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      analyticsPollInterval(pollScope, query.state.data, query.state.status === 'error'),
  });
}

export function useHistoryQuery(params: URLSearchParams) {
  useAnalyticsPollScope(JSON.stringify(historyQueryKey(params)));
  return useQuery({ ...historyQueryOptions(params), placeholderData: keepPreviousData });
}

// Available Models
export function useAvailableModelsQuery() {
  return useQuery({
    queryKey: ['availableModels'],
    queryFn: () => api.availableModels().then((res) => normalizeAvailableModels(res.data)),
    staleTime: 5 * 60 * 1000, // public catalog changes infrequently
  });
}

// One request and cache entry for both lists; each hook selects its own.
// The cached value is `{ models, videoModels }`: anything writing this key
// (setQueryData) must keep that shape.
const modelPricingQuery = {
  queryKey: ['modelPricing'],
  queryFn: () =>
    api.modelPricing().then((res) => ({
      models: normalizeModelPricing(res.models),
      videoModels: normalizeVideoModelPricing(res.video_models),
    })),
  staleTime: 5 * 60 * 1000, // matches gateway pricing cache TTL
};

export function useModelPricingQuery() {
  return useQuery({ ...modelPricingQuery, select: (pricing) => pricing.models });
}

export function useVideoModelPricingQuery() {
  return useQuery({ ...modelPricingQuery, select: (pricing) => pricing.videoModels });
}

// Passkeys
export function usePasskeysQuery() {
  return useQuery({
    queryKey: ['passkeys'],
    queryFn: () => api.passkeys(),
    staleTime: 30 * 1000,
    retry: false,
  });
}

type BackgroundPrefetchOptions = {
  signal?: AbortSignal;
};

const prefetchKeys = () =>
  queryClient.prefetchQuery({
    queryKey: ['keys'],
    queryFn: () => api.keys().then((res) => res.keys || []),
    staleTime: 60 * 1000,
  });

const prefetchOrganizations = () =>
  queryClient.prefetchQuery({
    queryKey: ['organizations'],
    queryFn: () =>
      api.organizations().then((res) => {
        if (Array.isArray(res)) return res;
        return res.organizations || [];
      }),
    staleTime: 60 * 1000,
  });

const prefetchEndpoints = () =>
  queryClient.prefetchQuery({
    queryKey: ['endpoints'],
    queryFn: () => api.endpoints().then((res) => res.endpoints || []),
    staleTime: 30 * 1000,
  });

const prefetchStats = (params = new URLSearchParams()) =>
  queryClient.prefetchQuery({
    queryKey: ['stats', params.toString()],
    queryFn: () => api.stats(params),
    staleTime: 10 * 1000,
  });

const prefetchHistory = (params = historyRequestParams()) =>
  queryClient.prefetchQuery(historyQueryOptions(params, false));

const prefetchAvailableModels = () =>
  queryClient.prefetchQuery({
    queryKey: ['availableModels'],
    queryFn: () => api.availableModels().then((res) => normalizeAvailableModels(res.data)),
    staleTime: 5 * 60 * 1000,
  });

const prefetchModelPricing = () => queryClient.prefetchQuery(modelPricingQuery);

const prefetchPasskeys = () =>
  queryClient.prefetchQuery({
    queryKey: ['passkeys'],
    queryFn: () => api.passkeys(),
    staleTime: 30 * 1000,
  });

// Prefetch helpers for hover/focus routing and background cache warmup.
export const prefetch = {
  keys: prefetchKeys,
  organizations: prefetchOrganizations,
  endpoints: prefetchEndpoints,
  stats: prefetchStats,
  history: prefetchHistory,
  availableModels: prefetchAvailableModels,
  modelPricing: prefetchModelPricing,
  passkeys: prefetchPasskeys,
  portalWarmup: ({ signal }: BackgroundPrefetchOptions = {}) =>
    runSequentialPrefetches(
      [prefetchOrganizations, prefetchEndpoints, prefetchAvailableModels],
      signal,
    ),
};

/** Return effect cleanup so route changes or foreground requests stop queued warmups. */
export function schedulePortalWarmup() {
  const controller = new AbortController();
  const idleId = window.setTimeout(() => {
    void prefetch.portalWarmup({ signal: controller.signal });
  }, 1000);
  return () => {
    window.clearTimeout(idleId);
    controller.abort();
  };
}

async function runSequentialPrefetches(tasks: Array<() => Promise<unknown>>, signal?: AbortSignal) {
  for (const [index, task] of tasks.entries()) {
    if (signal?.aborted) return;
    await task();
    if (signal?.aborted || index === tasks.length - 1) return;
    await delay(BACKGROUND_PREFETCH_GAP_MS, signal);
  }
}

function delay(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    function abort() {
      window.clearTimeout(timeoutId);
      resolve();
    }

    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', abort, { once: true });
  });
}

function normalizeAvailableModels(models?: AvailableModel[]) {
  if (!Array.isArray(models)) return [];
  return models.filter((model) => typeof model.id === 'string' && model.id.trim());
}

/** A video model the page can render safely; anything malformed is dropped. */
function normalizeVideoModelPricing(models?: PortalVideoModelPricing[]) {
  if (!Array.isArray(models)) return [];
  return models.filter(
    (model) =>
      typeof model.id === 'string' &&
      model.id.trim() &&
      typeof model.provider === 'string' &&
      model.pricing &&
      Number.isFinite(model.pricing.video) &&
      // Absent and null both mean "no separate 1080p rate".
      (model.pricing.video_1080p == null || Number.isFinite(model.pricing.video_1080p)) &&
      Array.isArray(model.supported_resolutions) &&
      model.supported_resolutions.every((resolution) => typeof resolution === 'string') &&
      Number.isFinite(model.min_duration_secs) &&
      Number.isFinite(model.max_duration_secs),
  );
}

function normalizeModelPricing(models?: PortalModelPricing[]) {
  if (!Array.isArray(models)) return [];
  return models.filter((model) => {
    const pricing = model.pricing;
    return (
      typeof model.id === 'string' &&
      model.id.trim() &&
      pricing &&
      Number.isFinite(pricing.input) &&
      Number.isFinite(pricing.output) &&
      (pricing.cache_read === null || Number.isFinite(pricing.cache_read)) &&
      (pricing.cache_write === null || Number.isFinite(pricing.cache_write))
    );
  });
}
